<script setup lang="ts">
import { useToastStore } from '../../stores/toast';
import { useI18n } from 'vue-i18n';
const toast = useToastStore();
const { t: translate } = useI18n();
</script>

<template>
  <div class="toast-container" aria-live="polite">
    <transition-group name="toast">
      <div
        v-for="t in toast.toasts"
        :key="t.id"
        class="toast"
        :class="'toast-' + t.type"
        :role="t.type === 'error' ? 'alert' : undefined"
        @click="toast.dismiss(t.id)"
      >
        <span class="toast-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path v-if="t.type === 'success'" d="m8 12 2.5 2.5L16 9" />
            <path v-else-if="t.type === 'error'" d="M12 7v6m0 3.5v.5" />
            <path v-else d="M12 11v5m0-8.5v.5" />
          </svg>
        </span>
        <span class="toast-message">{{ t.message }}</span>
        <button type="button" class="toast-close" :aria-label="translate('common.close')" @click.stop="toast.dismiss(t.id)">×</button>
      </div>
    </transition-group>
  </div>
</template>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(460px, calc(100vw - 32px));
  pointer-events: none;
}
.toast {
  --toast-accent: var(--brand-500);
  box-sizing: border-box;
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 12px 12px 14px;
  border: 1px solid color-mix(in srgb, var(--toast-accent) 26%, var(--border));
  border-radius: var(--radius);
  background: var(--surface);
  color: var(--foreground);
  box-shadow: var(--shadow-lg);
  font-size: 13px;
  font-weight: 550;
  line-height: 1.45;
  cursor: pointer;
  animation: toastIn 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}
.toast-icon {
  flex: none;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: color-mix(in srgb, var(--toast-accent) 12%, var(--surface));
  color: var(--toast-accent);
}
.toast-icon svg { width: 18px; height: 18px; }
.toast-message { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.toast-close {
  flex: none;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  font: inherit;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.toast-close:hover { background: var(--surface-soft); color: var(--foreground); }
.toast-close:focus-visible { outline: 2px solid var(--brand-500); outline-offset: 2px; }
.toast-info { --toast-accent: var(--brand-500); }
.toast-success { --toast-accent: var(--success); }
.toast-error { --toast-accent: var(--destructive); }
.toast-enter-active, .toast-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateY(-8px); }

@keyframes toastIn {
  from { opacity: 0; transform: translateY(-12px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 640px) {
  .toast-container { top: 12px; width: calc(100vw - 24px); }
  .toast { gap: 10px; padding: 12px; font-size: 14px; }
  .toast-close { width: 44px; height: 44px; }
}
@media (prefers-reduced-motion: reduce) {
  .toast { animation: none; }
  .toast-enter-active, .toast-leave-active, .toast-close { transition: none; }
}
</style>
